import { PropsWithChildren, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { prewarmPiiScan } from "@/lib/pii";
import { logger } from "@/lib/logger";
import usePiiFeatureEnabled from "@/hooks/usePiiFeatureEnabled";
import ConversationHubProvider from "./ConversationHubProvider";
import NotificationHubProvider from "./notification/NotificationHubProvider";
import SharePointProvider from "@/pages/workspace/providers/SharePointProvider";
import UploadHubProvider from "@/pages/workspace/providers/UploadHubProvider";
import LawTermsProvider from "./law-terms/LawTermsProvider";
import LegislationFilterConfigProvider from "./legislation-filter/LegislationFilterConfigProvider";
import { GlobalSearchProvider } from "./global-search/GlobalSearchProvider";
import { bindMsalCacheToAuth0User } from "@/services/microsoft/auth";

/**
 * Mounts the hub/context providers that only make sense for a signed-in user.
 *
 * The mount is one-way: once a session has settled as authenticated, the subtree
 * stays mounted for the lifetime of the SPA. Auth0 can drop `isAuthenticated`
 * back to false on an already-running session — `getAccessTokenSilently` always
 * dispatches `GET_ACCESS_TOKEN_COMPLETE` with `await client.getUser()`, and
 * auth0-spa-js calls `logout({ openUrl: false })` when silent auth answers
 * `login_required`, so the SDK user becomes undefined with no navigation away.
 * Unmounting the providers there dropped the contexts under consumers that
 * render outside `AuthenticationGuard` (the right-sidebar content `AppRouter`
 * renders from context), so they threw "useConversationHub must be used within a
 * ConversationHubProvider" and the data router replaced the app with its generic
 * error page. It also tore down live SignalR connections and in-flight chat
 * streams on what is usually a recoverable blip.
 *
 * Staying mounted is safe: each hub already gates its own connections and
 * fetches on `isAuthenticated`, and a real sign-out or re-login leaves via a
 * full-page redirect, which discards this tree anyway.
 */
const AuthenticatedProviders: React.FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const piiFeatureEnabled = usePiiFeatureEnabled();
  const auth0UserId = user?.sub;

  // Latched during render rather than in an effect so it flips in the same pass
  // as AuthenticationGuard's own isAuthenticated check. An effect would leave
  // one commit in which guarded routes render without these providers.
  const hasAuthenticatedRef = useRef(false);
  if (isAuthenticated && !isLoading) {
    hasAuthenticatedRef.current = true;
  }

  // Preload the PII NER model at app boot (LAR-27 v2, decision 5) so the chat
  // send-gate almost never has to block on a cold model. Fire-and-forget: a
  // failed prewarm is retried per-scan and the gate stays fail-closed anyway.
  // Skipped entirely while the PII-Redaction flag is off (no model download);
  // Unleash flags load async, so the effect re-runs if the flag flips on.
  useEffect(() => {
    if (!isAuthenticated || isLoading || !piiFeatureEnabled) return;
    prewarmPiiScan().catch((error: unknown) => {
      logger.warn("PII model prewarm failed; will retry on first scan", {
        operation: "piiPrewarm",
        error,
      });
    });
  }, [isAuthenticated, isLoading, piiFeatureEnabled]);

  // The Microsoft (MSAL) cache persists in localStorage across Auth0 sessions;
  // hand it to the signed-in user and drop it if someone else linked it (LAR-401).
  useEffect(() => {
    if (!isAuthenticated || isLoading || !auth0UserId) return;
    bindMsalCacheToAuth0User(auth0UserId).catch((error: unknown) => {
      logger.warn("Binding the MSAL cache to the Auth0 user failed", {
        operation: "bindMsalCacheToAuth0User",
        error,
      });
    });
  }, [isAuthenticated, isLoading, auth0UserId]);

  if (!hasAuthenticatedRef.current) {
    return <>{children}</>;
  }

  return (
    <NotificationHubProvider>
      <ConversationHubProvider>
        <SharePointProvider>
          <UploadHubProvider>
            <LawTermsProvider>
              <LegislationFilterConfigProvider>
                <GlobalSearchProvider>{children}</GlobalSearchProvider>
              </LegislationFilterConfigProvider>
            </LawTermsProvider>
          </UploadHubProvider>
        </SharePointProvider>
      </ConversationHubProvider>
    </NotificationHubProvider>
  );
};

export default AuthenticatedProviders;
